<template>
  <div>
    <b-modal
      modal-class="client-form-modal"
      body-class="bg-extra-light-gray p-0"
      footer-class="bg-extra-light-gray"
      v-model="editorShown"
      :title="''"
      @ok="onEdit()"
      @close="onCancel()"
      size="md-lg"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="w-100 d-flex flex-row justify-content-between align-items-center"
        >
          <h5 class="modal-title">Invoice for {{ patientName }}</h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="onCancel()"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <template v-slot:modal-footer>
        <div
          class="b-row d-flex justify-content-between align-items-center w-100"
        >
          <div class="b-col">
            <b-button @click="onDelete()" variant="danger"
              >Delete Invoice
            </b-button>
          </div>
          <div class="b-col text-right mt-1 mr-2">
            <b-button @click="onCancel()" variant="secondary">Cancel</b-button>
            <b-button class="ml-2" variant="primary" @click="onEdit()"
              >Edit
            </b-button>
            <b-button class="ml-2" variant="primary" @click="printSingle">
              Print
            </b-button>
            <b-button
              :disabled="invoiceData?.amountPaid === invoiceData?.totalAmount"
              class="ml-2"
              variant="success"
              @click="onAddPayment()"
              >Add Payment
            </b-button>
          </div>
        </div>
      </template>

      <div class="card m-3 px-4 py-2 rounded-lg b-grid justify-content-between">
        <b-row cols="2" align-v="center">
          <b-col>
            <p class="b-title h-auto h3">Invoice</p>
          </b-col>
          <b-col
            class="d-flex justify-content-end flex-column align-items-end text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pb-2">
              {{ systSettingsData?.organizationName ?? "" }}
            </div>
            <div style="line-height:15px; text-align: right">
              <div>
                {{ systSettingsData?.organizationAddressLine1 ?? "" }}
                {{ systSettingsData?.organizationAddressLine2 ?? "" }}
              </div>
              <div>{{ systSettingsData?.organizationCity ?? "" }}</div>
              <div>{{ organizationState }}</div>
              <div>{{ systSettingsData?.organizationZipCode ?? "" }}</div>
            </div>
          </b-col>
        </b-row>
        <hr />
        <b-row class="d-flex justify-content-between align-content-center">
          <b-col
            class="d-flex justify-content-start flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Bill To</div>
            <div v-for="item in billTo" :key="item.index">{{ item }}</div>
          </b-col>
          <b-col
            class="d-flex justify-content-end flex-column align-items-end text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Invoice #</div>
            <div>{{ invoiceData?.refNumber ?? "" }}</div>
            <div class="font-weight-bold pt-2">Issued</div>
            <div>{{ formatDateOnly(issuedDate) ?? "" }}</div>
            <div v-if="showDueDate" class="font-weight-bold pt-2">Due</div>
            <div v-if="showDueDate">{{ formatDateOnly(dueDate) ?? "" }}</div>
          </b-col>
        </b-row>
        <b-row class="d-flex justify-content-between align-content-center">
          <b-col
            class="d-flex justify-content-start flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Client</div>
            <div v-for="item in clientData" :key="item.index">{{ item }}</div>
          </b-col>
          <b-col
            class="d-flex justify-content-start flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Provider</div>
            <div v-for="item in providerData" :key="item.index">{{ item }}</div>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0" />
        <div class="w-100">
          <table class="w-100 text-black-50 lower-font-size">
            <thead>
              <tr>
                <th class="font-weight-bold w-25">Date</th>
                <th class="font-weight-bold w-25">Payment Type</th>
                <th class="font-weight-bold w-25">Description</th>
                <th class="font-weight-bold w-25 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in lineItemsData" :key="item.id">
                <td class="py-2">{{ formatDateOnly(item.date) }}</td>
                <td class="py-2">
                  {{
                    item.type == 0
                      ? "Co-Pay"
                      : item.type == 1
                      ? "Deductible/Co-Insurance"
                      : item.type == 3
                      ? "Patient Responsibility"
                      : ""
                  }}
                </td>
                <td class="py-2">{{ item.description }}</td>
                <td class="text-right py-2">
                  {{ toCurrencyValue(item.amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr class="my-0 py-0" />
        <b-row
          cols="2"
          class="py-2 text-black-50 lower-font-size text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2" align-h="end">
            Subtotal
          </b-col>
          <b-col align-h="end" cols="2">
            {{ toCurrencyValue(invoiceData?.totalAmount ?? "") }}
          </b-col>
        </b-row>
        <hr class="align-self-end my-0 py-0" style="width:35%" />
        <b-row
          cols="2"
          class="py-2 text-black-50 lower-font-size text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2" align-h="end">
            Total
          </b-col>
          <b-col align-h="end" cols="2">
            {{ toCurrencyValue(invoiceData?.totalAmount ?? "") }}
          </b-col>
        </b-row>
        <b-row
          cols="2"
          class="py-2 text-black-50 lower-font-size text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2" align-h="end">
            Amount Paid
          </b-col>
          <b-col align-h="end" cols="2">
            {{ toCurrencyValue(invoiceData?.amountPaid ?? "") }}
          </b-col>
        </b-row>
        <hr class=" align-self-end my-0 py-0" style="width:35%" />
        <b-row
          cols="2"
          class="py-2 text-black-50 font-weight-bold text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2">
            Balance
          </b-col>
          <b-col
            v-if="invoiceData != null"
            align-h="end"
            cols="2"
            style="font-size:20px;"
          >
            {{
              toCurrencyValue(
                invoiceData.totalAmount >= invoiceData.amountPaid
                  ? invoiceData.totalAmount - invoiceData.amountPaid
                  : 0
              )
            }}
          </b-col>
          <b-col v-else align-h="end" cols="2" style="font-size:20px;">
            {{ "" }}
          </b-col>
        </b-row>
        <b-row cols="1">
          <b-col cols="12">
            <div class="font-weight-bold lower-font-size text-black-50">
              Notes
            </div>
            <div
              class="w-100 lower-font-size"
              v-for="item in notes"
              :key="item.index"
            >
              {{ item }}
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import invoiceServer from "../../../server/invoice";
import systemSettingServer from "../../../server/systemSetting";
import { mapActions, mapState } from "vuex";

export default {
  name: "BillingDocumentInvoiceViewModal",
  props: ["showing", "id"],
  data() {
    return {
      error: "",
      issuedDate: null,
      dueDate: null,
      clientData: "",
      providerData: "",
      notes: "",
      invoiceData: null,
      lineItemsData: null,
      billTo: "",
      systSettingsData: null,
      showDueDate: false,
      patientName: "",
      organizationState: "",
      singleUrl: null,
      singleBlobUrl: null
    };
  },
  async mounted() {},
  methods: {
    ...mapActions("client", ["fetchStates"]),
    async fetchData(invoiceShowId) {
      if (invoiceShowId != null) {
        this.invoiceData = await invoiceServer.get(invoiceShowId);
        this.lineItemsData = this.invoiceData.lineItems;
        this.patientName = this.invoiceData.episodeOfCare.patient.name;
        this.issuedDate = this.invoiceData.dateFrom ?? null;
        this.dueDate = this.invoiceData.dateTo ?? null;
        this.clientData = this.invoiceData.clientData.split("\n");
        this.providerData = this.invoiceData.providerData.split("\n");
        this.notes = this.invoiceData.notes.split("\n");
        this.billTo = this.invoiceData.billTo?.split("\n");
        this.organizationState = this.systSettingsData?.organizationState
          ? this.states.find(
              s => s.value === this.systSettingsData?.organizationState
            ).data.abbreviation
          : "";

        this.singleUrl = invoiceServer.invoiceViewUrl(invoiceShowId);

        fetch(this.singleUrl.url, { headers: this.singleUrl.headers }).then(
          response => {
            response.blob().then(blob => {
              this.singleBlobUrl = URL.createObjectURL(blob);
            });
          }
        );
      }
    },
    onAddPayment() {
      this.$emit("payment", this.invoiceData);
    },
    onEdit() {
      this.$emit("edit", this.id);
    },
    onCancel() {
      this.$emit("close");
      this.editorShown = false;
    },
    onDelete() {
      this.$dialog
        .alert(
          {
            title: "Are you sure you want to delete this record?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await invoiceServer
            .delete(this.id)
            .then(async () => {
              this.editorShown = false;
              this.$emit("close");
            })
            .catch(e => console.error(e.message));
        });
    },
    printSingle() {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.zIndex = -100;
      iframe.style.width = "2250px";
      iframe.src = this.singleBlobUrl;
      iframe.onload = function() {
        iframe.contentWindow.onafterprint = function() {
          document.body.removeChild(iframe);
        };

        setTimeout(() => {
          iframe.contentWindow.print();
        }, 500);
      };

      document.body.appendChild(iframe);
    }
  },
  computed: {
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    ...mapState("client", {
      states: state =>
        state.states.map(s => ({
          label: s.name,
          value: s.id,
          data: s
        }))
    })
  },
  watch: {
    showing: {
      handler: async function(value) {
        if (value) {
          this.systSettingsData = await systemSettingServer.get();
          this.showDueDate = (!this.systSettingsData?.invoiceHideDueDate) ?? true;
          this.fetchStates();
          await this.fetchData(this.id);
        }
      },
      immediate: true
    }
  }
};
</script>
